/**
 * Schedules the demand polling around tab visibility and long-poll health.
 *
 * Two loops keep the demand list and the open demand current: the `demand-changes`
 * long poll and a periodic reconcile. Chrome only throttles a hidden tab to one tick a
 * minute, so a tab left open overnight kept the scale-to-zero instance and its
 * database awake while learning nothing. This scheduler stops both loops while the
 * page is hidden and, on returning to visible, restarts the long poll with a fresh
 * handshake, which reconciles exactly once. When only the periodic reconcile is
 * wanted it reconciles once directly (HS2-BZ9S6A).
 *
 * Every demand change, worker results included, now moves the change cursor
 * (HS2-HF0QT0), so the periodic reconcile is only a fallback: it is off while the
 * long poll runs and is healthy, and runs while the long poll is failing or
 * reconnecting, or when no long poll was started. It has no DOM or network
 * dependency of its own.
 */

export interface VisibilitySource {
  isHidden(): boolean;
  /** Listen for visibility changes; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

export interface PollTimers {
  setInterval(callback: () => void, milliseconds: number): unknown;
  clearInterval(handle: unknown): void;
}

/**
 * Starts one change long-poll loop (a handshake when `cursor` is undefined); returns its
 * stop function. The loop reports `health(false)` when a request fails and
 * `health(true)` when one succeeds again; a new loop counts as healthy until it fails.
 */
export type ChangePollStarter = (cursor: number | undefined, health: (healthy: boolean) => void) => () => void;

export interface VisibilityPollingOptions {
  visibility: VisibilitySource;
  /** Reconcile the demand list and the open demand. */
  reconcile(): unknown;
  reconcileMs: number;
  timers?: PollTimers;
}

export interface VisibilityPolling {
  /** Run the change long poll while visible; `cursor` resumes the first start only. */
  startChanges(start: ChangePollStarter, cursor?: number): void;
  stopChanges(): void;
  /** Run the periodic reconcile while visible and the long poll is not healthy. Idempotent. */
  startReconcile(): void;
  stopReconcile(): void;
}

/** The page document, when there is one that can dispatch events (not under node unit tests). */
const pageDocument = (): Document | undefined =>
  typeof document !== 'undefined' && typeof document.addEventListener === 'function' ? document : undefined;

/** The page's visibility; always visible where there is no event-capable `document`. */
export function documentVisibility(): VisibilitySource {
  return {
    isHidden: () => pageDocument()?.visibilityState === 'hidden',
    subscribe: (listener) => {
      const page = pageDocument();
      if (page === undefined) return () => undefined;
      page.addEventListener('visibilitychange', listener);
      return () => {
        page.removeEventListener('visibilitychange', listener);
      };
    },
  };
}

const globalTimers: PollTimers = {
  setInterval: (callback, milliseconds) => setInterval(callback, milliseconds),
  clearInterval: (handle) => {
    clearInterval(handle as ReturnType<typeof setInterval>);
  },
};

export function createVisibilityPolling(options: VisibilityPollingOptions): VisibilityPolling {
  const { visibility, reconcileMs } = options;
  const timers = options.timers ?? globalTimers;
  let changeStarter: ChangePollStarter | undefined;
  let stopChangePoll: (() => void) | undefined;
  /** Bumped per started loop so a stopped loop's late health report is ignored. */
  let changeRun = 0;
  let changeHealthy = false;
  let reconcileWanted = false;
  let reconcileTimer: unknown;
  let unsubscribe: (() => void) | undefined;
  let hidden = false;

  const haltReconcileTimer = (): void => {
    if (reconcileTimer === undefined) return;
    timers.clearInterval(reconcileTimer);
    reconcileTimer = undefined;
  };
  /** The fallback reconcile runs while wanted and visible, unless a healthy long poll covers it. */
  const syncReconcileTimer = (): void => {
    const covered = stopChangePoll !== undefined && changeHealthy;
    if (!reconcileWanted || hidden || covered) {
      haltReconcileTimer();
      return;
    }
    if (reconcileTimer !== undefined) return;
    reconcileTimer = timers.setInterval(() => {
      if (!visibility.isHidden()) void options.reconcile();
    }, reconcileMs);
  };
  const runChanges = (cursor?: number): void => {
    if (changeStarter === undefined || stopChangePoll !== undefined) return;
    const run = ++changeRun;
    changeHealthy = true;
    stopChangePoll = changeStarter(cursor, (healthy) => {
      if (run !== changeRun || changeHealthy === healthy) return;
      changeHealthy = healthy;
      syncReconcileTimer();
    });
    syncReconcileTimer();
  };
  const haltChanges = (): void => {
    changeRun++;
    stopChangePoll?.();
    stopChangePoll = undefined;
    changeHealthy = false;
  };

  const onVisibilityChange = (): void => {
    const nowHidden = visibility.isHidden();
    if (nowHidden === hidden) return; // a repeated event changes nothing
    hidden = nowHidden;
    if (hidden) {
      haltChanges();
      haltReconcileTimer();
      return;
    }
    // Whatever changed while hidden is caught up once: the long poll's handshake
    // reconciles, or, without a long poll, one direct reconcile.
    if (changeStarter !== undefined) runChanges();
    else if (reconcileWanted) void options.reconcile();
    syncReconcileTimer();
  };

  const syncSubscription = (): void => {
    const wanted = changeStarter !== undefined || reconcileWanted;
    if (wanted && unsubscribe === undefined) {
      hidden = visibility.isHidden();
      unsubscribe = visibility.subscribe(onVisibilityChange);
    } else if (!wanted && unsubscribe !== undefined) {
      unsubscribe();
      unsubscribe = undefined;
    }
  };

  return {
    startChanges(start, cursor) {
      haltChanges();
      changeStarter = start;
      syncSubscription();
      // Started while hidden: the cursor is dropped, and becoming visible handshakes.
      if (!hidden) runChanges(cursor);
    },
    stopChanges() {
      haltChanges();
      changeStarter = undefined;
      syncSubscription();
      // Without a long poll the periodic reconcile is the only way to learn of changes.
      syncReconcileTimer();
    },
    startReconcile() {
      reconcileWanted = true;
      syncSubscription();
      syncReconcileTimer();
    },
    stopReconcile() {
      reconcileWanted = false;
      haltReconcileTimer();
      syncSubscription();
    },
  };
}
